import { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEntityFinderContext } from "../context/EntityFinderContext";
import { Resource } from "@/bryntum-lib/models/Resource";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { getFontSize } from "@/react/context/bryntumContext";

export const ResourceTab = () => {
  const { data, handleClose } = useEntityFinderContext();
  const { entityManager } = useSwivuBryntumContext();
  const resources = data as Resource[];
  const containerRef = useRef<HTMLDivElement>(null);

  const gap = 12;
  const rowVirtualizer = useVirtualizer({
    count: resources.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => getFontSize() * 3 + gap,
    overscan: 8
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const handleResourceClick = (resource: Resource) => {
    entityManager.openEntityManager({ entityType: ENTITY_TYPES.RESOURCE, entityId: resource.id });
    handleClose();
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div ref={containerRef} className="flex-1 overflow-auto bg-background">
        {resources.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
            <svg className="w-12 h-12 mb-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p className="text-sm">No resources found</p>
          </div>
        ) : (
          <div className="relative w-full" style={{ height: `${totalSize}px` }}>
            {virtualItems.map((virtualRow) => {
              const resource = resources[virtualRow.index];
              return (
                <div
                  key={resource.id}
                  className="absolute left-0 top-0 w-full"
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                >
                  <div
                    className="px-4 flex items-center bg-card hover:opacity-90 cursor-pointer transition-all rounded-md"
                    style={{ height: `${virtualRow.size - gap}px`, marginBottom: `${gap}px` }}
                    onClick={() => handleResourceClick(resource)}
                  >
                    <div className="font-medium text-foreground line-clamp-1">{resource.name || "N/A"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
